import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { InformationPanelContext } from '@contexts/information-panel'
import ContentfulExternalLink from '@entries/contentful-external-link'
import ContentfulPDFLink from '@entries/contentful-pdf-link'
import IconClose from '@fortuneo/ui-icons/general/IconClose'
import { useGSAP } from '@gsap/react'
import { bold } from '@render-mark/bold'
import { paragraph } from '@render-node/paragraph'
import RichText from '@resolvers/rich-text'
import clsx from 'clsx/lite'
import gsap from 'gsap'
import React, { type FC, useContext, useEffect, useRef } from 'react'

import { ContentType } from '../entries/content-types'
import ContentfulDeprecatedPageAmplificationLink from '../entries/contentful-deprecated-page-amplification-link'
import ContentfulStaticPageAmplificationLink from '../entries/contentful-static-page-amplification-link'

const InformationPanel: FC = () => {
  const { panel, setPanel } = useContext(InformationPanelContext)

  const containerRef = useRef<HTMLDivElement>()
  const backgroundRef = useRef<HTMLButtonElement>()
  const panelRef = useRef<HTMLDivElement>()
  const cursorRef = useRef<HTMLDivElement>()

  const mm = gsap.matchMedia()

  const { contextSafe } = useGSAP()

  const handleClose = contextSafe(() => {
    if (cursorRef) {
      cursorRef.current.style.opacity = '0'
    }

    mm.add(
      '(max-width: 767px)',
      () => {
        gsap
          .timeline()
          .to(backgroundRef?.current, {
            duration: 0.6,
            ease: 'power4.inOut',
            css: {
              backdropFilter: 'blur(0px)',
            },
          })
          .to(
            panelRef?.current,
            {
              y: '100%',
              duration: 0.6,
              ease: 'power4.inOut',
              onComplete: () => {
                setPanel(undefined)
              },
            },
            '<'
          )
      },
      []
    )

    mm.add(
      '(min-width: 768px)',
      () => {
        gsap
          .timeline()
          .to(backgroundRef?.current, {
            duration: 0.6,
            ease: 'power4.inOut',
            css: {
              backdropFilter: 'blur(10px)',
            },
          })
          .to(
            panelRef?.current,
            {
              x: '100%',
              duration: 0.6,
              ease: 'power4.inOut',
              onComplete: () => {
                setPanel(undefined)
              },
            },
            '<'
          )
      },
      []
    )

    return () => {
      mm.revert()
    }
  })

  // Hide custom cursor when exiting the viewport
  const handleHideCursor = () => {
    if (!cursorRef) {
      return
    }

    cursorRef.current.style.opacity = '0'
  }

  // Animation on opening
  useGSAP(() => {
    if (!panel) {
      return
    }

    gsap
      .timeline()
      .to(backgroundRef.current, {
        duration: 0.6,
        ease: 'power4.inOut',
        css: {
          backdropFilter: 'blur(10px)',
        },
      })
      .to(
        panelRef.current,
        {
          x: 0,
          y: 0,
          duration: 0.6,
          ease: 'power4.inOut',
        },
        '<'
      )
  }, [panel])

  // Move custom cursor to real cursor position
  useEffect(() => {
    if (!panel || !cursorRef) {
      return
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!cursorRef.current) {
        return
      }

      cursorRef.current.style.opacity = '100'
      cursorRef.current.style.left = `${e.clientX}px`
      cursorRef.current.style.top = `${e.clientY}px`
    }

    document.addEventListener('mousemove', handleMouseMove)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
    }
  }, [panel])

  // Lock the scroll if the panel is open
  useEffect(() => {
    document.body.style.overflow = panel ? 'hidden' : ''

    return () => {
      document.body.style.overflow = ''
    }
  }, [panel])

  if (!panel) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className={clsx('fixed inset-0 z-[9999]', panel ? 'flex' : 'hidden')}
      onMouseLeave={handleHideCursor}
    >
      <button
        ref={backgroundRef}
        className="flex-1 cursor-none laptop:!backdrop-blur-none"
        type="button"
        onClick={handleClose}
      />
      <div
        ref={panelRef}
        className="absolute bottom-0 left-0 right-0 top-0 z-10 translate-y-full overflow-y-auto bg-white-50 p-11 text-left text-amplification-black-premium tablet:left-auto tablet:top-0 tablet:w-1/2 tablet:translate-x-full tablet:translate-y-0 laptop:w-1/3"
      >
        <div className="flex items-center justify-between pb-3">
          <p className="text-[22px] font-medium">Conditions</p>
          <button
            className="flex h-8 w-8 items-center justify-center rounded-full bg-black-50 hover:opacity-70"
            type="button"
            onClick={handleClose}
          >
            <IconClose fill="white" />
          </button>
        </div>
        <RichText
          content={panel?.content}
          options={{
            renderMark: {
              [MARKS.BOLD]: bold,
            },
            renderNode: {
              [BLOCKS.PARAGRAPH]: paragraph,
              [INLINES.ENTRY_HYPERLINK]: {
                [ContentType.ContentfulPdfLink]: ContentfulPDFLink,
                [ContentType.ContentfulExternalLink]: ContentfulExternalLink,
                [ContentType.ContentfulStaticPage]:
                  ContentfulStaticPageAmplificationLink,
                [ContentType.ContentfulDeprecatedPage]:
                  ContentfulDeprecatedPageAmplificationLink,
              },
            },
          }}
        />
      </div>
      <div
        ref={cursorRef}
        className="pointer-events-none fixed left-0 top-0 hidden h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black-50 opacity-0 laptop:flex"
      >
        <IconClose fill="white" />
      </div>
    </div>
  )
}

export default InformationPanel
